import React, { Component } from 'react';

export interface ChildReferenceProps<THtmlInstance> {
    /**
     * This event handler should be used to push the html element reference
     * up to the ChildReferences (using ChildReferences.getChildIndexListener(i)).
     *
     * This is generally used with lists of components where we need to manage focus
     * at the root of the custom component, like a dropdown.
     *
     * @title Instance Listener
     */
    instanceListener?: (instance: THtmlInstance | null) => void;
    onClick?: (e: React.MouseEventHandler<THtmlInstance>) => void;
    onKeyUp?: (e: React.KeyboardEventHandler<THtmlInstance>) => void;
    onKeyDown?: (e: React.KeyboardEventHandler<THtmlInstance>) => void;
    onBlur?: (e: React.FocusEventHandler<THtmlInstance>) => void;
}

export abstract class ChildReferenceComponent<
    TProps extends ChildReferenceProps<THtmlInstance>,
    THtmlInstance extends HTMLElement
> extends Component<TProps> {
    protected elementRef: THtmlInstance | undefined;
    /**
     * Determines whether or not the component should apply focus to itself
     * on load (this is generally required for initial loads)
     *
     * @readonly
     * @abstract
     */
    abstract get shouldApplyFocus(): boolean;

    /**
     * This will return all props required for keyboard, mouse & focus events
     * generally used with a ChildReferenceComponent to assist with focus control
     */
    get interactiveProps() {
        const { onBlur, onClick, onKeyDown, onKeyUp } = this.props;
        return {
            onBlur,
            onClick,
            onKeyDown,
            onKeyUp,
            ref: this.getReference,
        };
    }

    componentDidMount() {
        this.setFocus();
    }

    componentDidUpdate() {
        this.setFocus();
    }

    setFocus() {
        if (this.elementRef && this.shouldApplyFocus) {
            this.elementRef.focus();
        }
    }

    /**
     * This provides a means of extracting the reference of an element in the implementing class' render method,
     * which in turn allows the parent component to apply focus to this element should the need arise.
     */
    getReference = (instance: THtmlInstance | null) => {
        const { instanceListener } = this.props;
        this.elementRef = instance;
        if (instanceListener) {
            return instanceListener(instance);
        }
    };
}



// WEBPACK FOOTER //
// ./src/utils/accessibility/ChildReferenceComponent.tsx